// ...existing code...
import React, { useState, useCallback, useMemo } from "react";
import { UI_COLORS } from "../../config";
import { List } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import LoadingPlaceholder from "./LoadingPlaceholder";
import ErrorPlaceholder from "./ErrorPlaceholder";
import NoKeysPlaceholder from "./NoKeysPlaceholder";
import { useTranslation } from "../../i18n";
import SearchBar from "./SearchBar";
import KeyItem from "./KeyItem";
import { ERROR_ICON_STYLE, createPagination } from "./KeysList.utils";
import { useAppManager } from "../../hooks/useAppManager";

export default React.memo(function KeysList({
  allKeys = [],
  isLoading,
  error,
  onKeySelect,
  selectedKey,
  onClearSearch,
  getKeyById
}) {
const { userActions } = useAppManager();
  const { t } = useTranslation();
  const canRead = useMemo(
    () => userActions?.includes('dpp-qr-read'),
    [userActions]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [errorValue, setErrorValue] = useState(null);

  const displayedKeys = useMemo(() => allKeys || [], [allKeys]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchTerm(value);
      const mode = value.trim() !== "";
      setIsSearchMode(mode);
      if (!mode) onClearSearch?.();
    },
    [onClearSearch]
  );

  const handleSubmitValue = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!searchTerm.trim()) {
        setErrorValue(t("keysList.enter_value"));
        return;
      }
      setErrorValue(null);

      try {
        getKeyById?.(searchTerm.trim());
        setSearchTerm("");
      } catch (err) {
        console.error("Failed to get item by value:", err);
        setErrorValue(
          err?.response?.data?.message ||
            err?.message ||
            t("keysList.failed_get_by_value")
        );
      } finally {
        setIsSearchMode(false);
      }
    },
    [searchTerm, getKeyById, t]
  );

  const renderKeyItem = useCallback(
    (item) => (
      <KeyItem
        item={item}
        selectedKey={selectedKey}
        onKeySelect={onKeySelect}
      />
    ),
    [onKeySelect, selectedKey]
  );

  const listKeyContent = useMemo(() => {
    // Show no permission message if user can't read
    if (!canRead) return <NoKeysPlaceholder noPermission={true} />;
    
    // Show no keys message if list is empty
    if (!displayedKeys.length) return <NoKeysPlaceholder noPermission={false} />;

    return (
      <div className="flex-grow overflow-hidden mt-4">
        <div
          className="h-full flex flex-col min-h-0"
          style={{ minHeight: "350px" }}
        >
          <List
            className="scrollable-list"
            header={
              <h3
                className="text-lg font-medium mb-3"
                style={{ color: UI_COLORS.primary }}
              >
                {t("keysList.list_of_keys")}
              </h3>
            }
            size="large"
            dataSource={displayedKeys}
            renderItem={renderKeyItem}
            pagination={createPagination(displayedKeys, t)}
          />
        </div>
      </div>
    );
  }, [canRead, displayedKeys, renderKeyItem, t]);

  if (isLoading && !displayedKeys.length) {
    return (
      <LoadingPlaceholder
        tip={t("keysList.loading_tip")}
        subtitle={t("keysList.loading_subtitle")}
      />
    );
  }

  if (error) {
    return <ErrorPlaceholder error={error} onRetry={onClearSearch} />;
  }

  return (
    <div className="overflow-hidden flex flex-col h-full">
      {canRead && (<SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        onSubmit={handleSubmitValue}
        placeholder={t("keysList.search_placeholder")}
      />)}

      <div className="mt-2 text-xs text-gray-500">
        {isSearchMode ? (
          <span>
            <strong>{t("keysList.tip_label")}</strong> {t("keysList.tip_text")}
          </span>
        ) : null}
      </div>

      {errorValue && (
        <div className="flex items-center text-red-600 text-sm">
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={ERROR_ICON_STYLE}
          />
          {errorValue}
        </div>
      )}

      {listKeyContent}
    </div>
  );
});
